import { supabase } from './supabase';
import { BankTransaction, CashflowClassification } from '../types/database';

export interface CreateBankTransactionData {
  company_id: string;
  bank_account_id: string;
  accounting_category_id?: string | null;
  transaction_date: string;
  description: string;
  classification?: CashflowClassification; // Legacy field, optional now
  amount: number;
}

export interface UpdateBankTransactionData {
  bank_account_id?: string;
  accounting_category_id?: string | null;
  transaction_date?: string;
  description?: string;
  classification?: CashflowClassification; // Legacy field
  amount?: number;
  is_cc_settlement?: boolean;
}

export const bankTransactionsService = {
  /**
   * Get all bank transactions for a company with bank account and accounting category details
   */
  async getBankTransactions(companyId: string) {
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .select('*, bank_accounts(nickname), accounting_categories(name, cashflow_mapping)')
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false });

      if (error) {
        console.error('Get bank transactions error:', error);
        return { data: null, error };
      }

      return { data: data as BankTransaction[], error: null };
    } catch (err) {
      console.error('Get bank transactions exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Get a single bank transaction by ID
   */
  async getBankTransaction(transactionId: string) {
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .select('*, bank_accounts(nickname), accounting_categories(name, cashflow_mapping)')
        .eq('id', transactionId)
        .single();

      if (error) {
        console.error('Get bank transaction error:', error);
        return { data: null, error };
      }

      return { data: data as BankTransaction, error: null };
    } catch (err) {
      console.error('Get bank transaction exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Create a new bank transaction
   */
  async createBankTransaction(transactionData: CreateBankTransactionData) {
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .insert({
          company_id: transactionData.company_id,
          bank_account_id: transactionData.bank_account_id,
          accounting_category_id: transactionData.accounting_category_id || null,
          transaction_date: transactionData.transaction_date,
          description: transactionData.description,
          classification: transactionData.classification || null,
          amount: transactionData.amount,
        })
        .select('*, bank_accounts(nickname)')
        .single();

      if (error) {
        console.error('Create bank transaction error:', error);
        return { data: null, error };
      }

      return { data: data as BankTransaction, error: null };
    } catch (err) {
      console.error('Create bank transaction exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Update a bank transaction
   */
  async updateBankTransaction(transactionId: string, updates: UpdateBankTransactionData) {
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .update(updates)
        .eq('id', transactionId)
        .select('*, bank_accounts(nickname), accounting_categories(name, cashflow_mapping)')
        .single();

      if (error) {
        console.error('Update bank transaction error:', error);
        return { data: null, error };
      }

      return { data: data as BankTransaction, error: null };
    } catch (err) {
      console.error('Update bank transaction exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Delete a bank transaction
   */
  async deleteBankTransaction(transactionId: string) {
    try {
      const { error } = await supabase
        .from('bank_transactions')
        .delete()
        .eq('id', transactionId);

      if (error) {
        console.error('Delete bank transaction error:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Delete bank transaction exception:', err);
      return { error: err };
    }
  },

  /**
   * Get transactions by bank account
   */
  async getTransactionsByBankAccount(bankAccountId: string) {
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .select('*, bank_accounts(nickname)')
        .eq('bank_account_id', bankAccountId)
        .order('transaction_date', { ascending: false });

      if (error) {
        console.error('Get transactions by account error:', error);
        return { data: null, error };
      }

      return { data: data as BankTransaction[], error: null };
    } catch (err) {
      console.error('Get transactions by account exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Get transactions by date range
   */
  async getTransactionsByDateRange(companyId: string, startDate: string, endDate: string) {
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .select('*, bank_accounts(nickname)')
        .eq('company_id', companyId)
        .gte('transaction_date', startDate)
        .lte('transaction_date', endDate)
        .order('transaction_date', { ascending: false });

      if (error) {
        console.error('Get transactions by date range error:', error);
        return { data: null, error };
      }

      return { data: data as BankTransaction[], error: null };
    } catch (err) {
      console.error('Get transactions by date range exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Bulk delete transactions
   */
  async bulkDeleteTransactions(transactionIds: string[]) {
    try {
      const { error } = await supabase
        .from('bank_transactions')
        .delete()
        .in('id', transactionIds);

      if (error) {
        console.error('Bulk delete transactions error:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Bulk delete transactions exception:', err);
      return { error: err };
    }
  },

  /**
   * Bulk create transactions
   */
  async bulkCreateTransactions(transactions: CreateBankTransactionData[]) {
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .insert(transactions)
        .select('*, bank_accounts(nickname)');

      if (error) {
        console.error('Bulk create transactions error:', error);
        return { data: null, error };
      }

      return { data: data as BankTransaction[], error: null };
    } catch (err) {
      console.error('Bulk create transactions exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Bulk-ignore a set of bank transactions in a single DB round-trip.
   *
   * Sets status = 'ignored' for every ID in the supplied array, skipping any
   * rows that are already 'matched' (the DB RLS / app layer guard stays intact
   * for those). Returns the count of rows actually updated.
   */
  async bulkIgnoreTransactions(transactionIds: string[]): Promise<{ count: number; error: unknown | null }> {
    if (transactionIds.length === 0) return { count: 0, error: null };
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .update({ status: 'ignored' })
        // Only touch pending rows — never touch matched rows
        .in('id', transactionIds)
        .eq('status', 'pending')
        .select('id');

      if (error) {
        console.error('Bulk ignore transactions error:', error);
        return { count: 0, error };
      }

      return { count: (data ?? []).length, error: null };
    } catch (err) {
      console.error('Bulk ignore transactions exception:', err);
      return { count: 0, error: err };
    }
  },

  /**
   * Toggle transaction status between 'pending' and 'ignored'
   */
  async toggleTransactionStatus(transactionId: string, currentStatus: string) {
    try {
      // Don't allow toggling if it's matched
      if (currentStatus === 'matched') {
        return { 
          data: null, 
          error: { message: 'לא ניתן לשנות סטטוס של פעולה מותאמת' }
        };
      }

      const newStatus = currentStatus === 'ignored' ? 'pending' : 'ignored';

      const { data, error } = await supabase
        .from('bank_transactions')
        .update({ status: newStatus })
        .eq('id', transactionId)
        .select('*, bank_accounts(nickname)')
        .single();

      if (error) {
        console.error('Toggle transaction status error:', error);
        return { data: null, error };
      }

      return { data: data as BankTransaction, error: null };
    } catch (err) {
      console.error('Toggle transaction status exception:', err);
      return { data: null, error: err };
    }
  },
};
